/**
 * CampaignStats - Statistics dashboard for campaigns
 */
import {
  Users,
  PlayCircle,
  CheckCircle,
  LogOut,
  Mail,
  MousePointerClick,
  Eye,
} from 'lucide-react';
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  cn,
} from '@respectly/ui';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from 'recharts';
import type { CampaignStats as CampaignStatsType, CampaignStep } from '@respectly/shared-types';

// ============================================
// Types
// ============================================

interface CampaignStatsProps {
  stats: CampaignStatsType | null;
  steps: CampaignStep[];
  isLoading?: boolean;
  className?: string;
}

// ============================================
// Component
// ============================================

export function CampaignStats({
  stats,
  steps,
  isLoading,
  className,
}: CampaignStatsProps) {
  if (isLoading || !stats) {
    return <CampaignStatsSkeleton />;
  }

  const exitRate =
    stats.totalEnrolled > 0
      ? Math.round((stats.exitedEnrollments / stats.totalEnrolled) * 100)
      : 0;

  // Step drop-off bar chart
  const stepDropOffData = stats.stepMetrics.map((step, index) => ({
    name: `Step ${index + 1}`,
    type: steps[index]?.type ?? 'email',
    reached: step.reached,
    completed: step.completed,
    exitRate: Math.round(step.exitRate * 100),
  }));

  return (
    <div className={cn('space-y-6', className)}>
      {/* KPI Cards */}
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <StatCard
          title="Total Enrolled"
          value={stats.totalEnrolled}
          icon={Users}
          iconColor="text-blue-600 dark:text-blue-400"
          iconBg="bg-blue-100 dark:bg-blue-900/30"
        />
        <StatCard
          title="Active"
          value={stats.activeEnrollments}
          icon={PlayCircle}
          iconColor="text-amber-600 dark:text-amber-400"
          iconBg="bg-amber-100 dark:bg-amber-900/30"
        />
        <StatCard
          title="Completed"
          value={stats.completedEnrollments}
          icon={CheckCircle}
          iconColor="text-emerald-600 dark:text-emerald-400"
          iconBg="bg-emerald-100 dark:bg-emerald-900/30"
        />
        <StatCard
          title="Exit Rate"
          value={`${exitRate}%`}
          icon={LogOut}
          iconColor="text-red-600 dark:text-red-400"
          iconBg="bg-red-100 dark:bg-red-900/30"
          description={`${stats.exitedEnrollments} exited`}
        />
      </div>

      {/* Step drop-off chart */}
      {stepDropOffData.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Step Drop-off</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="h-[260px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={stepDropOffData}
                  margin={{ top: 4, right: 16, left: 0, bottom: 4 }}
                  barGap={4}
                >
                  <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="rgba(148,163,184,0.2)" />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <YAxis tick={{ fontSize: 11, fill: '#64748b' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: 'var(--background, #fff)',
                      border: '1px solid rgba(148,163,184,0.3)',
                      borderRadius: '8px',
                      fontSize: '12px',
                    }}
                    formatter={(value: number, name: string) => [
                      value.toLocaleString(),
                      name === 'reached' ? 'Reached' : 'Completed',
                    ]}
                  />
                  <Bar dataKey="reached" fill="#93c5fd" radius={[4, 4, 0, 0]} name="reached" />
                  <Bar dataKey="completed" fill="#34d399" radius={[4, 4, 0, 0]} name="completed" />
                </BarChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-center gap-4 mt-2">
              <div className="flex items-center gap-1.5">
                <div className="h-2.5 w-2.5 rounded-sm bg-blue-300" />
                <span className="text-xs text-slate-500">Reached</span>
              </div>
              <div className="flex items-center gap-1.5">
                <div className="h-2.5 w-2.5 rounded-sm bg-emerald-400" />
                <span className="text-xs text-slate-500">Completed</span>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Charts Row */}
      <div className="grid gap-6 lg:grid-cols-2">
        {/* Email performance */}
        {stats.emailMetrics && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Email Performance</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-3">
                  <MetricCard
                    icon={Mail}
                    label="Sent"
                    value={stats.emailMetrics.sent}
                  />
                  <MetricCard
                    icon={Eye}
                    label="Opened"
                    value={stats.emailMetrics.opened}
                    rate={stats.emailMetrics.openRate ? Math.round(stats.emailMetrics.openRate * 100) : undefined}
                  />
                  <MetricCard
                    icon={MousePointerClick}
                    label="Clicked"
                    value={stats.emailMetrics.clicked}
                    rate={stats.emailMetrics.clickRate ? Math.round(stats.emailMetrics.clickRate * 100) : undefined}
                  />
                </div>

                {stats.emailMetrics.sent > 0 && (
                  <div className="space-y-2 pt-2">
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>Open rate</span>
                      <span className="font-medium text-slate-700 dark:text-slate-300 tabular-nums">
                        {Math.round((stats.emailMetrics.openRate ?? 0) * 100)}%
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-indigo-500 rounded-full"
                        style={{ width: `${Math.round((stats.emailMetrics.openRate ?? 0) * 100)}%` }}
                      />
                    </div>
                    <div className="flex items-center justify-between text-xs text-slate-500">
                      <span>Click rate</span>
                      <span className="font-medium text-slate-700 dark:text-slate-300 tabular-nums">
                        {Math.round((stats.emailMetrics.clickRate ?? 0) * 100)}%
                      </span>
                    </div>
                    <div className="h-1.5 bg-slate-100 dark:bg-slate-800 rounded-full overflow-hidden">
                      <div
                        className="h-full bg-emerald-500 rounded-full"
                        style={{ width: `${Math.round((stats.emailMetrics.clickRate ?? 0) * 100)}%` }}
                      />
                    </div>
                  </div>
                )}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Step table */}
        {stats.stepMetrics.length > 0 && (
          <Card>
            <CardHeader>
              <CardTitle className="text-base">Step Performance</CardTitle>
            </CardHeader>
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b dark:border-slate-700">
                      <th className="text-left py-2.5 px-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Step</th>
                      <th className="text-right py-2.5 px-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Reached</th>
                      <th className="text-right py-2.5 px-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Done</th>
                      <th className="text-right py-2.5 px-4 text-xs font-medium text-slate-500 uppercase tracking-wide">Exit</th>
                    </tr>
                  </thead>
                  <tbody>
                    {stats.stepMetrics.map((stepStat, index) => {
                      const step = steps[index];
                      const cr =
                        stepStat.reached > 0
                          ? Math.round((stepStat.completed / stepStat.reached) * 100)
                          : 0;
                      const er = (stepStat.exitRate * 100).toFixed(1);
                      return (
                        <tr key={index} className="border-b dark:border-slate-700 last:border-0 hover:bg-slate-50 dark:hover:bg-slate-800/50">
                          <td className="py-2.5 px-4">
                            <div className="text-sm font-medium text-slate-900 dark:text-white">
                              Step {index + 1}
                            </div>
                            <div className="text-xs text-slate-400 capitalize">{step?.type ?? 'unknown'}</div>
                          </td>
                          <td className="text-right py-2.5 px-4 text-sm tabular-nums text-slate-600 dark:text-slate-400">
                            {stepStat.reached.toLocaleString()}
                          </td>
                          <td className="text-right py-2.5 px-4 text-sm tabular-nums">
                            <span className={cn(
                              'font-medium',
                              cr >= 80 ? 'text-emerald-600 dark:text-emerald-400' : cr >= 50 ? 'text-amber-600 dark:text-amber-400' : 'text-red-600 dark:text-red-400'
                            )}>
                              {cr}%
                            </span>
                          </td>
                          <td className="text-right py-2.5 px-4 text-sm tabular-nums text-slate-500">
                            {er}%
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}

// ============================================
// Sub Components
// ============================================

interface StatCardProps {
  title: string;
  value: number | string;
  icon: typeof Users;
  iconColor: string;
  iconBg: string;
  description?: string;
}

function StatCard({ title, value, icon: Icon, iconColor, iconBg, description }: StatCardProps) {
  return (
    <Card>
      <CardContent className="p-4">
        <div className="flex items-center gap-3">
          <div className={cn('h-10 w-10 rounded-lg flex items-center justify-center flex-shrink-0', iconBg)}>
            <Icon className={cn('h-5 w-5', iconColor)} />
          </div>
          <div>
            <div className="text-xs text-slate-500 dark:text-slate-400">{title}</div>
            <div className="text-2xl font-bold text-slate-900 dark:text-white tabular-nums leading-tight">
              {typeof value === 'number' ? value.toLocaleString() : value}
            </div>
            {description && <div className="text-xs text-slate-400">{description}</div>}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

interface MetricCardProps {
  icon: typeof Mail;
  label: string;
  value: number;
  rate?: number;
}

function MetricCard({ icon: Icon, label, value, rate }: MetricCardProps) {
  return (
    <div className="text-center p-3 bg-slate-50 dark:bg-slate-800 rounded-lg">
      <Icon className="h-4 w-4 text-slate-400 mx-auto mb-1" />
      <div className="text-lg font-bold text-slate-900 dark:text-white tabular-nums">
        {value.toLocaleString()}
      </div>
      <div className="text-xs text-slate-500">{label}</div>
      {rate !== undefined && (
        <div className="text-xs font-semibold text-indigo-600 dark:text-indigo-400 mt-0.5 tabular-nums">
          {rate}%
        </div>
      )}
    </div>
  );
}

// ============================================
// Skeleton
// ============================================

function CampaignStatsSkeleton() {
  return (
    <div className="space-y-6">
      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        {Array.from({ length: 4 }).map((_, i) => (
          <Card key={i}>
            <CardContent className="p-4">
              <div className="flex items-center gap-3">
                <div className="h-10 w-10 rounded-lg bg-slate-200 dark:bg-slate-700 animate-pulse flex-shrink-0" />
                <div className="space-y-2">
                  <div className="h-3 w-16 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
                  <div className="h-6 w-12 bg-slate-200 dark:bg-slate-700 rounded animate-pulse" />
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
      <Card>
        <CardContent className="p-6">
          <div className="h-[260px] bg-slate-100 dark:bg-slate-800 rounded animate-pulse" />
        </CardContent>
      </Card>
    </div>
  );
}
